import React, { useEffect, useState } from 'react';
import { CheckIcon, CopyIcon, LogOutIcon, RotateCcwIcon } from 'lucide-react';
import { ScheduleFields, emptySchedule, scheduleToEpoch, type ScheduleDraft } from './ScheduleFields';
import { browserTimeZone, epochToZonedFields, formatSchedule } from '../utils/schedule';
interface EventSettingsProps {
  roomCode: string;
  eventName: string;
  chatEnabled: boolean;
  closed: boolean;
  joinedCount: number;
  winnersCount: number;
  startsAt: number | null;
  timeZone: string | null;
  onSetSchedule: (startsAt: number | null, timeZone: string) => void;
  onRename: (name: string) => void;
  onSetChatEnabled: (enabled: boolean) => void;
  onSetClosed: (closed: boolean) => void;
  onClearWinners: () => void;
  onCloseEvent: () => void;
}
export function EventSettings({
  roomCode,
  eventName,
  chatEnabled,
  closed,
  joinedCount,
  winnersCount,
  startsAt,
  timeZone,
  onSetSchedule,
  onRename,
  onSetChatEnabled,
  onSetClosed,
  onClearWinners,
  onCloseEvent
}: EventSettingsProps) {
  const [draft, setDraft] = useState(eventName);
  const [copied, setCopied] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [schedule, setSchedule] = useState<ScheduleDraft>(emptySchedule);
  useEffect(() => {
    const zone = timeZone ?? browserTimeZone();
    setSchedule(startsAt ? {
      ...epochToZonedFields(startsAt, zone),
      timeZone: zone
    } : {
      date: '',
      time: '',
      timeZone: zone
    });
  }, [startsAt, timeZone]);
  const draftStart = scheduleToEpoch(schedule);
  const scheduleChanged = draftStart !== startsAt || schedule.timeZone !== (timeZone ?? browserTimeZone());
  useEffect(() => setDraft(eventName), [eventName]);
  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1600);
    return () => window.clearTimeout(t);
  }, [copied]);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(roomCode);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return <section aria-label="Event settings" className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto pr-1" data-id="element-123" data-mp="3r2sx54akwdo4">
      <div data-id="element-124" data-mp="3ccbujhm7jhpn">
        <label htmlFor="live-event-name" className="text-sm font-semibold text-white" data-id="element-125" data-mp="3cclmvbuq60yi">
          Event name
        </label>
        <div className="mt-2 flex gap-2" data-id="element-126" data-mp="3hadcfz7wdazj">
          <input id="live-event-name" value={draft} maxLength={60} onChange={e => setDraft(e.target.value)} className="min-w-0 flex-1 rounded-lg border border-line bg-raised px-3 py-2.5 text-sm text-white focus:border-gold focus:outline-none" data-id="element-127" data-mp="33c5j01a2krt4" />
          <button type="button" onClick={() => onRename(draft.trim() || 'Live draw')} disabled={draft.trim() === eventName || draft.trim().length === 0} className="shrink-0 rounded-lg bg-gold px-3.5 text-sm font-semibold text-ink transition-[background-color,transform] duration-150 ease-emphasis hover:bg-[#F0C154] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold active:scale-[0.97] disabled:cursor-not-allowed disabled:bg-line disabled:text-muted disabled:active:scale-100" data-id="element-128" data-mp="2yen9o9s54akl">
            Save
          </button>
        </div>
      </div>

      <div data-id="element-129" data-mp="2m1jhrgfrm2y1">
        <p className="text-sm font-semibold text-white" data-id="element-130" data-mp="2zuhizjew2p9i">Invitation code</p>
        <div className="mt-2 flex items-center justify-between gap-3 rounded-lg border border-line bg-surface px-4 py-3" data-id="element-131" data-mp="2zwu9wm2mk9yp">
          <span className="font-display text-2xl tracking-[0.2em] text-gold" data-id="element-132" data-mp="39soxlvlpgv8c">{roomCode}</span>
          <button type="button" onClick={copy} className="inline-flex items-center gap-1.5 text-xs font-semibold text-body transition-colors duration-150 ease-emphasis hover:text-gold focus-visible:outline focus-visible:outline-1 focus-visible:outline-gold" data-id="element-133" data-mp="34tufywzh5cpt">
            {copied ? <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-134" data-mp="1bxrxudns6ljo" /> : <CopyIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-135" data-mp="1gvjv7n772zv9" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <p className="mt-2 text-xs text-muted" data-id="element-136" data-mp="1lt3b0rjjjkpb">
          {joinedCount} participant{joinedCount === 1 ? '' : 's'} joined with this code.
        </p>
      </div>

      <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-line bg-surface p-4" data-id="element-137" data-mp="t7jvhxrwnz4j">
        <input type="checkbox" checked={chatEnabled} onChange={e => onSetChatEnabled(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#E3B23C]" data-id="element-138" data-mp="y6dci06ei89g" />
        <span data-id="element-139" data-mp="17w6miwqwafbl">
          <span className="block text-sm font-semibold text-white" data-id="element-140" data-mp="17wpv3munnrcy">
            Chat and comments {chatEnabled ? 'on' : 'paused'}
          </span>
          <span className="mt-1 block text-xs text-muted" data-id="element-141" data-mp="fekhk07rucz3">
            Turn this off mid-draw to freeze the room; existing messages stay visible.
          </span>
        </span>
      </label>

      <div className="rounded-lg border border-line bg-surface p-4" data-id="element-142" data-mp="2oo1sehi3iv4f">
        <p className="text-sm font-semibold text-white" data-id="element-143" data-mp="2olnwk6fldevt">Scheduled start</p>
        <p className="mt-1 text-xs text-muted" data-id="element-144" data-mp="2jn49u3t6bz2e">
          {startsAt && startsAt > Date.now() ? `Participants are watching a countdown to ${formatSchedule(startsAt, schedule.timeZone)}.` : 'Set a date, time, and zone to hold participants on a countdown before the draw.'}
        </p>
        <div className="mt-3" data-id="element-145" data-mp="24y8267v14o2b">
          <ScheduleFields idPrefix="live" value={schedule} onChange={setSchedule} data-id="element-146" data-mp="20y7fy11ov8kp" />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-4 text-xs" data-id="element-147" data-mp="1vz375biofcuw">
          <button type="button" onClick={() => onSetSchedule(draftStart, schedule.timeZone)} disabled={!scheduleChanged} className="rounded-lg bg-gold px-3.5 py-2 font-semibold text-ink transition-[background-color,transform] duration-150 ease-emphasis hover:bg-[#F0C154] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold active:scale-[0.97] disabled:cursor-not-allowed disabled:bg-line disabled:text-muted disabled:active:scale-100" data-id="element-148" data-mp="1w0xrooq1d2yg">
            Save schedule
          </button>
          {startsAt !== null && <button type="button" onClick={() => onSetSchedule(null, schedule.timeZone)} className="font-semibold text-body transition-colors duration-150 ease-emphasis hover:text-crimson focus-visible:outline focus-visible:outline-1 focus-visible:outline-gold" data-id="element-149" data-mp="1v3srtju2j6x5">
              Start now (clear schedule)
            </button>}
        </div>
      </div>

      <div className="rounded-lg border border-line bg-surface p-4" data-id="element-150" data-mp="2e2543ega0wwy">
        <p className="text-sm font-semibold text-white" data-id="element-151" data-mp="2e0u0bemtzp5u">
          Winners published · {winnersCount}
        </p>
        <p className="mt-1 text-xs text-muted" data-id="element-152" data-mp="7eefp8h0mbhr">
          Clearing removes the winners list from every participant screen and puts every
          name back into the draw.
        </p>
        {confirmClear ? <div className="mt-3 flex items-center gap-4 text-xs" data-id="element-153" data-mp="h4r090oty4nw">
            <button type="button" onClick={() => {
          onClearWinners();
          setConfirmClear(false);
        }} className="font-semibold text-crimson hover:text-[#E0655E] focus-visible:outline focus-visible:outline-1 focus-visible:outline-gold" data-id="element-154" data-mp="h6akt3hir1vh">
              Clear for everyone?
            </button>
            <button type="button" onClick={() => setConfirmClear(false)} className="text-muted hover:text-white focus-visible:outline focus-visible:outline-1 focus-visible:outline-gold" data-id="element-155" data-mp="3ukf81k6um7qj">
              Cancel
            </button>
          </div> : <button type="button" onClick={() => setConfirmClear(true)} className="mt-3 inline-flex items-center gap-2 rounded-lg border border-line px-3.5 py-2 text-xs font-semibold text-body transition-colors duration-150 ease-emphasis hover:border-crimson hover:text-crimson focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold" data-id="element-156" data-mp="14vwfbdmy4sx5">
            <RotateCcwIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-157" data-mp="3i0ry0tz2m1dh" />
            Clear winners
          </button>}
      </div>

      <div className="rounded-lg border border-line bg-surface p-4" data-id="element-158" data-mp="3m0sc2di9cak0">
        <p className="text-sm font-semibold text-white" data-id="element-159" data-mp="3r0olwtdmyw7h">
          Event is {closed ? 'closed' : 'live'}
        </p>
        <p className="mt-1 text-xs text-muted" data-id="element-160" data-mp="3vz87uts8c7zu">
          {closed ? 'Participants see a closed notice, keep the winners list, and can read the chat but not post.' : 'Closing ends the draw for everyone: the participant screen switches to a closed notice and chat becomes read-only.'}
        </p>
        {closed ? <button type="button" onClick={() => onSetClosed(false)} className="mt-3 inline-flex items-center gap-2 rounded-lg bg-gold px-3.5 py-2 text-xs font-semibold text-ink transition-[background-color,transform] duration-150 ease-emphasis hover:bg-[#F0C154] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold active:scale-[0.97]" data-id="element-161" data-mp="1u4txy53xoczo">
            Reopen the event
          </button> : confirmClose ? <div className="mt-3 flex items-center gap-4 text-xs" data-id="element-162" data-mp="233fc7o2dh5un">
            <button type="button" onClick={() => {
          onSetClosed(true);
          setConfirmClose(false);
        }} className="font-semibold text-crimson hover:text-[#E0655E] focus-visible:outline focus-visible:outline-1 focus-visible:outline-gold" data-id="element-163" data-mp="231uhnyg25mws">
              Close it for everyone?
            </button>
            <button type="button" onClick={() => setConfirmClose(false)} className="text-muted hover:text-white focus-visible:outline focus-visible:outline-1 focus-visible:outline-gold" data-id="element-164" data-mp="n6br6qop7t2c">
              Cancel
            </button>
          </div> : <button type="button" onClick={() => setConfirmClose(true)} className="mt-3 inline-flex items-center gap-2 rounded-lg border border-crimson/60 px-3.5 py-2 text-xs font-semibold text-crimson transition-colors duration-150 ease-emphasis hover:bg-crimson hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold" data-id="element-165" data-mp="s3bwv0hmytsx">
            Close the event
          </button>}
      </div>

      <div className="border-t border-line pt-4" data-id="element-166" data-mp="3hlufnz5zhjwn">
        <button type="button" onClick={onCloseEvent} className="inline-flex items-center gap-2 text-xs font-semibold text-body transition-colors duration-150 ease-emphasis hover:text-crimson focus-visible:outline focus-visible:outline-1 focus-visible:outline-gold" data-id="element-167" data-mp="3cn0zu0385gtb">
          <LogOutIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-168" data-mp="1iffbux1vl89j" />
          Switch to another event
        </button>
        <p className="mt-2 text-[11px] text-muted" data-id="element-169" data-mp="1ncp0iahfxfwb">
          The event stays open — participants keep their access to this code.
        </p>
      </div>
    </section>;
}